/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useOptionalAuth } from "../lib/auth";
import { apiUrl as getApiUrl } from "../lib/runtimeConfig";

type ApiFunctions = {
  get: (endpoint: string) => Promise<any> | any;
  post: (endpoint: string, data?: any) => Promise<any> | any;
  put: (endpoint: string, data: any) => Promise<any> | any;
  patch: (endpoint: string, data: any) => Promise<any> | any;
  del: (endpoint: string, data?: any) => Promise<any> | any;
  download: (endpoint: string, filename: string) => Promise<boolean>;
};

const uninitialized = () => {
  throw new Error("ApiDataProvider missingz");
};
const ApiDataContext = createContext<ApiFunctions>({
  get: uninitialized,
  post: uninitialized as any,
  put: uninitialized as any,
  patch: uninitialized as any,
  del: uninitialized,
  download: uninitialized as any,
});

export const useApiData = () => useContext(ApiDataContext);

type ApiDataProviderProps = {
  children: ReactNode;
  isPublic?: boolean;
};

// The public pages (card, respond) have no AuthProvider and never send a token.
const noAuth = {
  isAuthenticated: false,
  getAccessToken: async (): Promise<string> => {
    throw new Error("no AuthProvider");
  },
};

export const ApiDataProvider: React.FC<ApiDataProviderProps> = ({ children, isPublic = false }) => {
  const { getAccessToken, isAuthenticated } = useOptionalAuth() ?? noAuth;
  const [token, setToken] = useState<string | null>(null);
  const apiUrl = getApiUrl();

  // The token is fetched per request: the SDK caches it and refreshes it when it expires (Logto
  // access tokens live for an hour), so a long session never sends a stale token.
  const buildHeaders = useCallback(async () => {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (!isPublic && isAuthenticated) {
      try {
        h.Authorization = `Bearer ${await getAccessToken()}`;
      } catch (e) {
        console.error("Error getting access token", e);
      }
    }
    return h;
  }, [isPublic, isAuthenticated, getAccessToken]);

  const del = useCallback(
    async (endpoint: string, data?: any) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "DELETE",
          headers: await buildHeaders(),
          ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return false;
        }

        return response.json();
      } catch (ex) {
        console.error("API request failed", ex);
        return false;
      }
    },
    [apiUrl, buildHeaders]
  );

  // Authenticated file download: fetches a binary/JSON payload with the bearer token attached and
  // saves it via a temporary object URL. Used for the GDPR data export (Art. 15/20), which a plain
  // browser navigation could not request because it cannot send the Authorization header.
  const download = useCallback(
    async (endpoint: string, filename: string) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "GET",
          headers: await buildHeaders(),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return false;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return true;
      } catch (ex) {
        console.error("API request failed", ex);
        return false;
      }
    },
    [apiUrl, buildHeaders]
  );

  const get = useCallback(
    async (endpoint: string) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "GET",
          headers: await buildHeaders(),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return false;
        }

        return response.json();
      } catch (ex) {
        console.error("API request failed", ex);
        return false;
      }
    },
    [apiUrl, buildHeaders]
  );

  const post = useCallback(
    async (endpoint: string, data?: any) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "POST",
          headers: await buildHeaders(),
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return null;
        }

        return response.json();
      } catch (ex) {
        console.error("API request failed", ex);
        return null;
      }
    },
    [apiUrl, buildHeaders]
  );

  const patch = useCallback(
    async (endpoint: string, data: any) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "PATCH",
          headers: await buildHeaders(),
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return null;
        }

        return response.json();
      } catch (ex) {
        console.error("API request failed", ex);
        return null;
      }
    },
    [apiUrl, buildHeaders]
  );

  const put = useCallback(
    async (endpoint: string, data: any) => {
      try {
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: "PUT",
          headers: await buildHeaders(),
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          console.error(`API request failed: ${response.status}`);
          return null;
        }

        return response.json();
      } catch (ex) {
        console.error("API request failed", ex);
        return null;
      }
    },
    [apiUrl, buildHeaders]
  );

  useEffect(() => {
    const getToken = async () => {
      if (isAuthenticated) {
        try {
          const accessToken = await getAccessToken();
          setToken(accessToken);
        } catch (e) {
          console.error("Error getting access token", e);
        }
      }
    };
    if (!isPublic) {
      getToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token && !isPublic) {
    return null;
  }
  return (
    <ApiDataContext.Provider
      value={{
        get,
        post,
        put,
        patch,
        del,
        download,
      }}
    >
      {children}
    </ApiDataContext.Provider>
  );
};
